import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.min.css';

const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [eventFilter, setEventFilter] = useState('all');
  const [events, setEvents] = useState([]);

    const [search, setSearch] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1)


  useEffect(() => {
    fetchTickets();
    fetchEvents();
  }, []);

    useEffect(() => { fetchTickets(1); }, [statusFilter, eventFilter, dateFilter, debouncedSearch]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(search); // only set after user stops typing for 500ms
        }, 500);

        return () => clearTimeout(handler);
    }, [search]);


    const fetchTickets = async (pageNumber = 1) => {
        try {
            setLoading(true);
            const params = {
                page: pageNumber,
                status: statusFilter !== 'all' ? statusFilter : undefined,
                event_id: eventFilter !== 'all' ? eventFilter : undefined,
                date: dateFilter || undefined,
                search: search || undefined,
            };
            const response = await api.get('/organizer/tickets', { params });

            // Assuming your API response structure is similar to orders
            const paginator = response.data.data.tickets;
            setTickets(paginator.data || []);
            setPagination(paginator);
            setStats(response.data.data.stats || {});
            setPage(pageNumber);
        } catch (error) {
            toast.error('Failed to load tickets');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchEvents = async () => {
    try {
      const response = await api.get('/organizer/events');
      setEvents(response.data.data.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

    const renderPagination = () => {
        if (!pagination.last_page || pagination.last_page <= 1) return null;

        const current = pagination.current_page;
        const last = pagination.last_page;
        const maxPages = 3;

        let start = Math.max(1, current - Math.floor(maxPages / 2));
        let end = start + maxPages - 1;

        if (end > last) {
            end = last;
            start = Math.max(1, end - maxPages + 1);
        }

        const pageNumbers = [];
        for (let i = start; i <= end; i++) pageNumbers.push(i);

        return (
            <nav className="mt-4">
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${current === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link text-gray-900"
                            onClick={() => fetchTickets(current - 1)}
                        >
                            Previous
                        </button>
                    </li>

                    {start > 1 && (
                        <>
                            <li className="page-item">
                                <button
                                    className="page-link text-gray-900"
                                    onClick={() => fetchTickets(1)}
                                >
                                    1
                                </button>
                            </li>
                            {start > 2 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                        </>
                    )}

                    {pageNumbers.map(num => (
                        <li
                            key={num}
                            className={`page-item ${num === current ? 'active' : ''}`}
                        >
                            <button
                                className={`page-link text-gray-900 ${
                                    num === current
                                        ? '!bg-yellow-400 !text-white !border-yellow-400 border'
                                        : ''
                                }`}
                                onClick={() => fetchTickets(num)}
                            >
                                {num}
                            </button>
                        </li>
                    ))}

                    {end < last && (
                        <>
                            {end < last - 1 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                            <li className="page-item">
                                <button
                                    className="page-link text-gray-900"
                                    onClick={() => fetchTickets(last)}
                                >
                                    {last}
                                </button>
                            </li>
                        </>
                    )}

                    <li className={`page-item ${current === last ? 'disabled' : ''}`}>
                        <button
                            className="page-link text-gray-900"
                            onClick={() => fetchTickets(current + 1)}
                        >
                            Next
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

    const handleResetFilters = () => {
        setSearch('');
        setEventFilter('all');
        setDateFilter('');
        setStatusFilter('all')
    };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge-success',
      used: 'badge-info',
      cancelled: 'badge-danger',
      transferred: 'badge-warning'
    };
    return badges[status] || 'badge-secondary';
  };

  const getStatusIcon = (status) => {
    const icons = {
      active: 'fa-check-circle',
      used: 'fa-ticket-alt',
      cancelled: 'fa-times-circle',
      transferred: 'fa-exchange-alt'
    };
    return icons[status] || 'fa-question-circle';
  };

  const getTicketLabel = (ticket) => {
    if (ticket.ticket_category?.category_name) return ticket.ticket_category.category_name;
    if (ticket.seat_map_seat_id) {
      return [ticket.section?.name, ticket.seat?.label].filter(Boolean).join(' · ') || 'Reserved Seat';
    }
    if (ticket.seat_map_section_id) {
      return (ticket.section?.name || 'General Admission') + ' (GA)';
    }
    return 'Ticket';
  };

    const handleExportCSV = async () => {
        try {
            const params = {
                status: statusFilter !== 'all' ? statusFilter : undefined,
                event_id: eventFilter !== 'all' ? eventFilter : undefined,
                date: dateFilter || undefined,
                search: search || undefined,
            };

            const response = await api.get('/organizer/tickets/export', {
                params,
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = `tickets_${new Date().toISOString().split('T')[0]}.csv`;
            link.click();

            toast.success('Tickets exported successfully');
        } catch (error) {
            toast.error('Export failed');
            console.error(error);
        }
    };

    return (
    <div>
      {/* Page Heading */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h5 className="h5 mb-0 text-gray-900 font-weight-bold">
          Tickets Management
        </h5>
        <button 
          className="btn btn-success btn-sm"
          onClick={handleExportCSV}
          disabled={tickets.length === 0}
        >
          <i className="fas fa-download mr-2"></i>
          Export CSV
        </button>
      </div>

      {/* Stats Row */}
      <div className="row mb-4">
        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-yellow shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-gray-900 text-uppercase mb-1">Total Tickets</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.total_tickets || 0}</div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-ticket-alt fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-yellow shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-gray-900 text-uppercase mb-1">Active Tickets</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.active_tickets || 0}</div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-check-circle fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-yellow shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-gray-900 text-uppercase mb-1">Used Tickets</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.used_tickets || 0}</div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-user-check fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-yellow shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-gray-900 text-uppercase mb-1">Cancelled Tickets</div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">{stats.cancelled_tickets || 0}</div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-times-circle fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

        <div className="bg-white rounded-lg shadow p-6 mb-4 border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Search */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by user , ticket category or event ..."
                        className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-yellow-400"
                    />
                </div>

                {/* Event Filter */}
                {/*<div>*/}
                {/*    <label className="block text-sm font-medium text-gray-700 mb-2">Event</label>*/}
                {/*    <select*/}
                {/*        value={eventFilter}*/}
                {/*        onChange={(e) => setEventFilter(e.target.value)}*/}
                {/*        className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-yellow-400"*/}
                {/*    >*/}
                {/*        <option value="all">All Events</option>*/}
                {/*        {events.map(event => (*/}
                {/*            <option key={event.id} value={event.id}>{event.title}</option>*/}
                {/*        ))}*/}
                {/*    </select>*/}
                {/*</div>*/}

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-yellow-400"
                    >
                        <option value="all">All Statuses</option>
                        <option value="active">Active</option>
                        <option value="used">Used</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="transferred">Transferred</option>
                    </select>
                </div>

                {/* Date Filter */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                    <Flatpickr
                        value={dateFilter}
                        onChange={(dates, dateStr) => {
                            setDateFilter(dateStr); // 👈 use string, not Date object
                        }}
                        options={{
                            dateFormat: 'Y-m-d',
                            maxDate: 'today'
                        }}
                        placeholder="Select a date"
                        className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-yellow-400"
                    />
                </div>

                {/* Reset Button */}
                <div className="flex items-end">
                    <button
                        onClick={handleResetFilters}
                        className="btn btn-outline-danger w-20"
                        disabled={!search && eventFilter === 'all' && !dateFilter && statusFilter === 'all'}
                    >
                        <i className="fas fa-redo mr-2"></i>Reset
                    </button>
                </div>
            </div>
        </div>

      {/* Tickets Table Card */}
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <div className="row align-items-center">
            <div className="col">
              <h6 className="m-0 font-weight-bold text-gray-900 font-weight-bold">All Tickets</h6>
            </div>
          </div>
        </div>
        <div className="card-body p-4">
            {loading ? (
                    <div className="text-center py-12">
                        <svg
                            className="animate-spin h-12 w-12 mx-auto text-yellow-400"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            ></circle>
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                        <p className="mt-4 text-gray-600">Loading tickets...</p>
                    </div>
                ) :
          <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
            <table className="table table-hover mb-0">
              <thead className="thead-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  <th style={{ fontSize: '0.8rem' }}>Ticket #</th>
                  <th style={{ fontSize: '0.8rem' }}>Event</th>
                  <th style={{ fontSize: '0.8rem' }}>Category</th>
                  <th style={{ fontSize: '0.8rem' }}>Attendee</th>
                    <th style={{ fontSize: '0.8rem' }}>Price</th>
                  <th style={{ fontSize: '0.8rem' }}>Order #</th>
                  <th style={{ fontSize: '0.8rem' }}>Purchase Date</th>
                  <th style={{ fontSize: '0.8rem' }}>Scanned</th>
                </tr>
              </thead>
              <tbody>
                {tickets.length > 0 ? (
                  tickets.map((ticket) => (
                    <tr key={ticket.id} style={{ fontSize: '0.85rem' }}>
                      <td className="font-weight-bold text-yellow-400">
                        #{ticket.id}
                      </td>
                      <td>
                        <div className="text-truncate" style={{ maxWidth: '180px' }}>
                          {ticket.event?.title}
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-primary">
                          {getTicketLabel(ticket)}
                        </span>
                      </td>
                      <td>
                        <div>
                          <div className="font-weight-bold">{ticket.user?.first_name} {ticket.user?.last_name}</div>
                          <small className="text-muted">{ticket.user?.email}</small>
                        </div>
                      </td>
                        <td>
                            <small className="font-weight-bold">{ticket.amount} {ticket.event.currency}</small>
                        </td>
                      <td>
                        <small className="text-muted">#{ticket.order?.id}</small>
                      </td>
                      <td>{formatDate(ticket.created_at)}</td>
                      <td>
                        {ticket.scanned_at ? (
                          <div>
                            <i className="fas fa-check-circle text-success mr-1"></i>
                            <small>{formatDate(ticket.scanned_at)}</small>
                          </div>
                        ) : (
                          <span className="text-muted small">Not scanned</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      {searchTerm ? 'No tickets found matching your search' : 'No tickets yet'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
            }
        </div>
          {renderPagination()}
      </div>
    </div>
  );
};

export default Tickets;
